import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OrderCreateInput } from '../_interfaces/order';

@Injectable({
  providedIn: 'root',
})
export class OrderService {

  private editingOrderID = new BehaviorSubject<number | null>(null);
  private searchParams: any = new BehaviorSubject<any>(null); 


  constructor(private http: HttpClient) {}

  createOrder(params: any): Observable<any> {
    const reqData: OrderCreateInput = {
      customerId: params.customerId,
      orderAmount: params.orderAmount,
      discAmount: params.discAmount,
      shipCost: params.shipCost,
      handleCharge: params.handleCharge,
      netAmount: params.netAmount,
      userpoints: params.userpoints,
      orderItems: params.orderItems,
      billingAddressId: params.billingAddressId,
      shippingAddressId: params.shippingAddressId,
      freeSample: params.freeSample,
      paymentMethod: params.paymentMethod,
      customerBAddress: params.customerBAddress,
      customerSAddress: params.customerSAddress,
      deliveryTerms: params.deliveryTerms,
      deliveryMethod: params.deliveryMethod,
      customerMode: params.customerMode,
      discountCoupons: params.discountCoupons,
      system: 'BO',
      vatNumber: params.vatNumber,
      subscribeNewsletter: false,
      subscribeTexts: false,
      deliveryType: params.deliveryType,
      orderNotes: params.orderNotes,
    };
    const data = this.http.post<OrderCreateInput>('/orders/create', reqData);
    return data;
  }

  updateOrder(params: any): Observable<any> {
    const reqData: any = {
      orderId: params.orderId,
      customerId: params.customerId,
      orderAmount: params.orderAmount,
      discAmount: params.discAmount,
      shipCost: params.shipCost,
      handleCharge: params.handleCharge,
      netAmount: params.netAmount,
      userpoints: params.userpoints,
      orderItems: params.orderItems,
      billingAddressId: params.billingAddressId,
      shippingAddressId: params.shippingAddressId,
      freeSample: params.freeSample,
      customerBAddress: params.customerBAddress,
      customerSAddress: params.customerSAddress,
      deliveryTerms: params.deliveryTerms,
      deliveryMethod: params.deliveryMethod,
      customerMode: params.customerMode,
      discountCoupons: params.discountCoupons,
      deliveryType: params.deliveryType,
      productUpdate: params.productUpdate,  
      vatNumber: params.vatNumber,
      deliveryMethodUpdate: params.deliveryMethodUpdate,
    };
    const data = this.http.post<any>('/orders/update/backoffice', reqData);
    return data;
  }

  updateAgent(orderId: number, agentId: number): Observable<any> {
    const params: any = {
        orderId: orderId,
        agentId: agentId
      }
    const data = this.http.post<any>(`/orders/update/agent`, params);
    return data;
  }

  getOrders(params: any) {
    const data = this.http.post('/orders/filter',params);
    return data;
  }

  getSampleOrders(params: any) {
    const data = this.http.post('/orders/samples',params);
    return data;
  }

   getAbandonedOrders(params: any) {
    const data = this.http.post('/orders/abandoned',params);
    return data;
  }


  getOrderStatusUpdates(params: any) {
    const data = this.http.post('/orders/status/updates',params);
    return data;
  }


   getBestSellers(params: any) {
    const data = this.http.post('/orders/best-sellers',params);
    return data;
  }

  getDashboardAnalytics(params: any) {
    const data = this.http.post('/orders/analytics/dashboard', params);
    return data;
  }

  // Live "needs attention" figures, system record counts, and extra views of the date range.
  getDashboardOverview(params: any) {
    const data = this.http.post('/orders/analytics/overview', params);
    return data;
  }

  // Profit, m², customer types, delivery, areas, samples, returns, carts, views vs sales.
  getDashboardInsights(params: any) {
    return this.http.post('/orders/analytics/insights', params);
  }

  getOrdersByCutomer() {
    const data = this.http.get('/orders/customer');
    return data;
  }

  getOrderById(orderId: number) {
    const data = this.http.post('/orders', { orderId });
    return data;
  }

  getOrderByEncrptedId(orderId: string) {
    const data = this.http.post('/orders/encrypted', { orderId });
    return data;
  }

  getKlarnaDetailsByOrderId(orderId: number) {
    const data = this.http.get('/orders/klarna/details/' + orderId);
    return data;
  }

  verifyTeyaPayment(orderId: number) {
    const data = this.http.post('/orders/verify/teya', { orderId });
    return data;
  }

  sendPaymentLink(orderId: number) {
    const data = this.http.post('/orders/send-pay-link', { orderId });
    return data;
  }

  emailOrder(orderId: number, orderStatus: string) {
    const data = this.http.post('/orders/email', { orderId, orderStatus });
    return data;
  }

  updateOrderItemStore(orderItemId: number, store: string) {
    const data = this.http.put(`/orders/update/order-item/${orderItemId}`, { store });
    return data;
  }

  // notifyCustomer: email the customer when closing out (Completed /
  // Delayed Completion); left out, no email is sent.
  updateOrderStatus(orderId: number, orderStatus: string, notifyCustomer?: boolean) {
    const data = this.http.put(`/orders/update/order-details/${orderId}`, { orderStatus, notifyCustomer });
    return data;
  }

  updateOrderCustomerNote(orderId: number, orderNotes: string) {
    const data = this.http.put(`/orders/note/update/${orderId}`, { orderNotes });
    return data;
  }

  updateOrderTracking(orderId: number, params: { trackingMethod: string; trackingNumber: string; trackingStatus: string }) {
    const data = this.http.put(`/orders/update/tracking/${orderId}`, params);
    return data;
  }

  getOrderNotes(orderId:number) {
    const data = this.http.get(`/orders/notes/${orderId}`);
    return data;
  }

  // Customers who took free samples and haven't placed a real order since.
  getSampleLeads(params: any) {
    return this.http.post<any>('/orders/sample-leads', params);
  }

  // Customers who started an order but didn't go through with it (pending / cancelled).
  getLostSales(params: any) {
    return this.http.post<any>('/orders/lost-sales', params);
  }

  // "Ideas for this hour" on the Sales page: who to call, what to pitch, how today is going.
  // Orders placed in a period with a given status - the list behind the
  // status counts under "Today so far".
  getOrdersByStatus(params: any) {
    return this.http.post<any>('/sales-ideas/orders', params);
  }

  // Super admins: what PayPal holds for this order's tracking (read-only).
  verifyPaypalTracking(orderId: number) {
    return this.http.get<any>(`/orders/paypal-tracking/verify/${orderId}`);
  }

  // Super admins: bulk catch-up of PayPal tracking for Completed PayPal
  // orders. dryRun lists what would be sent without sending anything.
  resyncPaypalTracking(body: { from: string; to: string; dryRun: boolean }) {
    return this.http.post<any>('/orders/paypal-tracking/resync', body);
  }

  getSalesIdeas() {
    return this.http.get<SalesIdeas>('/sales-ideas');
  }

  // Website checkouts that got an email or phone but no order.
  getAbandonedCarts(params: any) {
    return this.http.post<any>('/orders/abandoned-carts', params);
  }

  saveAbandonedCartFollowUp(cartId: number, note: string) {
    return this.http.post<any>(`/orders/abandoned-carts/${cartId}/follow-up`, { note });
  }

  // The cart at today's prices, ready to load into a new sale.
  getAbandonedCartBasket(cartId: number) {
    return this.http.get<any>(`/orders/abandoned-carts/${cartId}/basket`);
  }

  createOrderNote( params: {orderId: number, orderNote: string}) {
    const data = this.http.post('/orders/notes', params);
    return data;
  }

  deleteOrderNote (orderNoteID:number) {
    const data = this.http.delete(`/orders/notes/delete/${orderNoteID}`);
    return data;
  }

  syncOrder (orderId:number) {
    const data = this.http.post(`/orders/sync`, { orderId });
    return data;
  }

  refundOrder (params: { orderId: number; amount: number; reason : string; stockAdjusted: boolean; items: any}) {
    const data = this.http.post(`/orders/refund`, params);
    return data;
  }

  getRefundOrder (orderId: number): Observable<any> {
    const data = this.http.get(`/orders/refund/${orderId}`);
    return data;
  }

  cancelOrder(orderId: number) {
    const data = this.http.post('/orders/cancel', { orderId });
    return data;
  }

  verifySample(email: string, phoneNo: string, postCode: string, orderId?: number): Observable<any> {
    const data = this.http.post('/orders/verify-sample', { email, phoneNo, postCode, orderId });
    return data;
  }

   updateOrderEmail(orderId: number,addressType: string, email: string) {
    const data = this.http.post(`/orders/email/update`, { orderId, addressType, email });
    return data;
  }

  private editingOrderNo: string | null = null;
  setEditingOrderID(orderID: number, orderNo?: string | null) {
    this.editingOrderNo = orderNo != null ? String(orderNo) : null;
    this.editingOrderID.next(orderID);
  }
  getEditingOrderNo(): string | null { return this.editingOrderNo; }

  // Suggestions from order history: what is often bought with these products,
  // and what this customer has bought before.
  getRecommendations(params: { productIds: number[]; customerId?: number }) {
    return this.http.post<RecommendationResult>('/orders/recommendations', params);
  }

  getOrderIdByNumber(orderNo: string) {
    return this.http.post<{ orderId: number; orderNo: string }>('/orders/id-by-number', { orderNo });
  }
  getEditingOrderID(): Observable<number | null> { return this.editingOrderID.asObservable(); }  

  // The list the order was opened from, so the edit page can step to the
  // previous / next order in it.
  private orderNav: OrderNavContext | null = null;
  setOrderNav(nav: OrderNavContext | null) { this.orderNav = nav; }
  getOrderNav(): OrderNavContext | null { return this.orderNav; }

  setSearchParams(params: any) { this.searchParams.next(params); }
  getSearchParams(): Observable<any> { return this.searchParams.asObservable(); }

}

export interface OrderNavContext {
  orders: { orderId: number; orderNo: string }[];  // the page that's showing, in display order
  params: any;         // the filter used to fetch that page
  pageIndex: number;
  pageSize: number;
  count: number;       // total orders matching the filter
}

export interface RecommendedProduct {
  productId: number;
  productName: string;
  productSlug: string;
  image: string | null;
  from: number;
  inStock: boolean;
  saving?: number;        // best discount, in %
  label?: string | null;  // e.g. "Stock Clearance"
  note?: string;          // why it's suggested, e.g. "Bought together in 3 orders"
  timesOrdered?: number;  // customerHistory
  totalQty?: number;
  lastOrdered?: string;
}

// One shelf of suggestions, from one angle of the order history.
export interface RecommendationLane {
  key: string;
  title: string;
  subtitle: string;
  icon: string;
  tone: 'amber' | 'violet' | 'orange' | 'sky' | 'emerald' | 'rose' | 'indigo';
  items: RecommendedProduct[];
}

export interface RecommendationResult {
  basedOn: number;
  lanes: RecommendationLane[];
  customerHistory: RecommendedProduct[];
}

export interface SalesIdeas {
  generatedAt: string;
  hour: string;
  nextUpdate: string;
  callNext: {
    kind: 'cart' | 'pending' | 'sample';
    id: number;
    orderNo: string | null;
    name: string;
    phone: string | null;
    email: string | null;
    value: number;
    reason: string;
    // What people in their county are viewing most on the website this week.
    nearby: { county: string; productId: number; productName: string; productSlug: string; views: number } | null;
  }[];
  pitch: {
    kind: 'rising' | 'viewed' | 'trending' | 'offer' | 'stock' | 'season';
    productId: number;
    productName: string;
    productSlug: string;
    image: string | null;
    from: number;
    reason: string;
  }[];
  pace: { weekday: string; orders: number; revenue: number; usualOrders: number; usualRevenue: number } | null;
  tip: { kind: 'busy-now' | 'busy-soon' | 'quiet' | 'normal'; text: string } | null;
  // Agents' own orders today (not website orders), biggest takings first.
  topAgents?: { agentId: number; name: string; orders: number; revenue: number }[];
  // Orders placed today, counted by their current status.
  statusCounts?: { status: string; count: number; value: number }[];
  // Counties viewing the most since yesterday, each with its most viewed product.
  hotspots: {
    county: string;
    region: string;
    views: number;
    topProduct: { productId: number; productName: string; productSlug: string; views: number } | null;
  }[];
}
